import { Button, IconButton, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useLayoutEffect } from 'react';
import { useBoolean } from 'usehooks-ts';

import { HeaderMobile } from '@/components/Header/HeaderMobile';
import { Icon } from '@/components/Icon/Icon';
import { DrawerHeader } from '@/components/Layout';

import { useTranslate } from '@/hooks/useTranslate';

import { ConsentDocument } from '@/types/api/user';
import { SourceType } from '@/types/document';

import { getDataTestId } from '@/utils/browser';
import { downloadCrossOriginFile } from '@/utils/file';

import {
  DownloadDocumentButton,
  ReviewDocumentActions,
  ReviewDocumentBody,
  ReviewDocumentDrawerRoot,
  ReviewDocumentFile,
  ReviewDocumentScrollArea,
  ReviewDocumentScrollContainer,
  ReviewDocumentScrollFader,
} from './ReviewAndSubmit.styled';

type ReviewDocumentDrawerProps = {
  open: boolean;
  document: ConsentDocument | null;
  currentIndex: number;
  totalDocuments: number;
  onClose: () => void;
  onAgree: () => void;
  onDecline: () => void;
};

export function ReviewDocumentDrawer({
  open,
  document,
  currentIndex,
  totalDocuments,
  onClose,
  onAgree,
  onDecline,
}: Readonly<ReviewDocumentDrawerProps>) {
  const { t } = useTranslate();
  const {
    value: isBottom,
    setTrue: setIsBottom,
    setFalse: resetIsBottom,
  } = useBoolean(false);

  useLayoutEffect(() => {
    if (open && document) {
      resetIsBottom();
      return;
    }

    resetIsBottom();
  }, [open, document, resetIsBottom]);

  if (!document) {
    return null;
  }

  const stepLabel = `${String(currentIndex).padStart(2, '0')}/${String(totalDocuments).padStart(2, '0')}`;

  const onDownload = async () => {
    const translatedTitle = t(
      `moreInfo.form.reviewAndSubmit.consentDocuments.${document.consentType}`,
    );
    const documentTitle = new DOMParser().parseFromString(
      translatedTitle,
      'text/html',
    ).body.textContent;

    try {
      await downloadCrossOriginFile(
        document.url,
        t('moreInfo.form.reviewAndSubmit.documentDrawer.downloadFileName', {
          documentTitle,
          version: document.currentVersion,
        }),
      );
    } catch (error) {
      console.error('Failed to download consent document:', error);
    }
  };

  return (
    <ReviewDocumentDrawerRoot
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop={false}
      {...getDataTestId('review-document-drawer')}
    >
      <HeaderMobile
        bgColor={theme => theme.palette.background.paper}
        leftSlot={
          <IconButton
            onClick={onClose}
            {...getDataTestId('review-document-drawer-close')}
          >
            <Icon
              name="close"
              color={(theme: Theme) => theme.palette.grey[700]}
            />
          </IconButton>
        }
        centerSlot={
          <Typography variant="body1Medium" color="text.primary">
            {stepLabel}
          </Typography>
        }
      />

      <DrawerHeader>
        <IconButton
          onClick={onClose}
          {...getDataTestId('review-document-drawer-close-desktop')}
        >
          <Icon
            name="close"
            color={(theme: Theme) => theme.palette.grey[700]}
          />
        </IconButton>
        <Typography variant="body1Medium" sx={{ flex: 1, textAlign: 'center' }}>
          {stepLabel}
        </Typography>
        <IconButton sx={{ visibility: 'hidden' }} tabIndex={-1}>
          <Icon name="close" />
        </IconButton>
      </DrawerHeader>

      <ReviewDocumentBody>
        <ReviewDocumentScrollContainer>
          <ReviewDocumentScrollArea
            {...getDataTestId('review-document-scroll-area')}
          >
            <ReviewDocumentFile
              src={document.url}
              srcType={SourceType.PDF}
              withViewButton={false}
              onPDFScrolledToBottom={setIsBottom}
            />
          </ReviewDocumentScrollArea>
          {!isBottom && <ReviewDocumentScrollFader />}
        </ReviewDocumentScrollContainer>

        <ReviewDocumentActions>
          <Button
            fullWidth
            disabled={!isBottom}
            onClick={onAgree}
            {...getDataTestId('review-document-agree-button')}
          >
            {t('moreInfo.form.reviewAndSubmit.documentDrawer.button.agree')}
          </Button>
          <DownloadDocumentButton
            fullWidth
            variant="outlined"
            startIcon={<Icon name="download" />}
            onClick={onDownload}
            {...getDataTestId('review-document-download-button')}
          >
            {t('common.download')}
          </DownloadDocumentButton>
          <Button
            fullWidth
            variant="outlined"
            onClick={onDecline}
            {...getDataTestId('review-document-decline-button')}
          >
            {t('moreInfo.form.reviewAndSubmit.documentDrawer.button.decline')}
          </Button>
        </ReviewDocumentActions>
      </ReviewDocumentBody>
    </ReviewDocumentDrawerRoot>
  );
}
